import { useState } from 'react';

import {
  Avatar,
  Button,
  Center,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  type MenuProps,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  LuChevronDown,
  LuChevronRight,
  LuChevronUp,
  LuLogOut,
  LuSettings,
  LuUserCheck,
  LuUserCircle,
} from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { Icon } from '@/components';
import { RoleType } from '@/services/api';

import { useGetAccountQuery } from '../auth/useAuthQueries';
import { ADMIN_PATH } from './constants';

export const AdminNavBarAccountMenu = ({
  ...rest
}: Omit<MenuProps, 'children'>) => {
  const navigate = useNavigate();
  const account = useGetAccountQuery();
  const { t } = useTranslation(['auth']);

  const ACCOUNT_MENU_OPTIONS = [
    ...(account.data?.isSynapsAdmin
      ? [
          {
            title: t('auth:admin.layout.accountMenu.synapsAdmin'),
            icon: LuUserCheck,
            url: `${ADMIN_PATH}/synaps-admin`,
            colorScheme: 'brand',
          },
        ]
      : []),
    ...(account.data?.role !== RoleType.Expert
      ? [
          {
            title: t('auth:admin.layout.accountMenu.accountSettings'),
            icon: LuUserCircle,
            url: `${ADMIN_PATH}/settings/account`,
            colorScheme: 'brand',
          },
        ]
      : []),
    {
      title: t('auth:admin.layout.accountMenu.emailSettings'),
      icon: LuSettings,
      url: `${ADMIN_PATH}/settings/communication`,
      colorScheme: 'brand',
    },
    {
      title: t('auth:admin.layout.accountMenu.logout'),
      icon: LuLogOut,
      url: `/logout?redirect=${ADMIN_PATH}/login`,
      colorScheme: 'gray',
    },
  ] as const;

  const [isOpen, setIsOpen] = useState(false);

  return (
    <Menu
      placement="bottom-end"
      {...rest}
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
    >
      <MenuButton
        borderColor={isOpen ? 'gray.100' : 'gray.50'}
        borderRadius="full"
        borderWidth={1}
        bg="transparent"
        h="12"
        px="1"
        minW="24"
        isLoading={account.isLoading}
        as={Button}
        variant="unstyled"
      >
        <HStack justifyContent="space-between" w="full">
          <Avatar src={account.data?.profileImage} name={account.data?.name} />
          <Stack gap={0} alignItems="flex-start">
            <Text color="gray.900" fontSize="sm" fontWeight="600">
              {account.data?.name + ' ' + (account.data?.surname ?? '')}
            </Text>
            <Text
              color="gray.500"
              fontWeight="400"
              fontSize="xs"
              isTruncated
              maxWidth="100px"
            >
              {account.data?.email}
            </Text>
          </Stack>
          <Stack
            h="10"
            w="10"
            justifyContent="center"
            alignItems="center"
            borderRadius="full"
          >
            <Icon
              icon={isOpen ? LuChevronUp : LuChevronDown}
              fontSize="lg"
              color="gray.600"
            />
          </Stack>
        </HStack>
      </MenuButton>

      <MenuList
        minWidth="17rem"
        overflow="hidden"
        p={3}
        borderColor="gray.50"
        shadow="md"
        borderRadius={12}
      >
        <MenuGroup
          title={account.data?.email ?? ''}
          noOfLines={1}
          m={0}
          color="gray.900"
          fontWeight="normal"
        />
        <MenuDivider />

        <VStack gap={3}>
          {ACCOUNT_MENU_OPTIONS.map((item) => (
            <MenuItem
              key={item.url}
              p={1}
              border="1px solid"
              borderColor="gray.50"
              transition="linear 0.1s"
              borderRadius={8}
              fontWeight="medium"
              _hover={{
                borderColor: `${item.colorScheme}.200`,
                bg: `${item.colorScheme}.25`,
              }}
              icon={
                <Center
                  w="2.25rem"
                  h="2.25rem"
                  bg={`${item.colorScheme}.50`}
                  borderRadius={8}
                >
                  <Icon
                    icon={item.icon}
                    fontSize="xl"
                    color={`${item.colorScheme}.500`}
                  />
                </Center>
              }
              onClick={() => navigate(item.url)}
            >
              <HStack justifyContent="space-between">
                <Text color="gray.900"> {item.title}</Text>
                {item.colorScheme !== 'gray' && (
                  <Icon icon={LuChevronRight} fontSize="lg" color="gray.500" />
                )}
              </HStack>
            </MenuItem>
          ))}
        </VStack>
      </MenuList>
    </Menu>
  );
};
